import React, { useState, useRef, useEffect } from 'react';
import './multi-autocomplete-style.css';

const MultiAutocompleteSelect = ({
  value = [],
  onChange,
  options = [],
  placeholder = "Оберіть значення",
  loading = false,
  loadingMore = false,
  hasMore = false,
  onSearch,
  onLoadMore,
  error,
  label,
  disabled = false,
  className = '',
  fieldsValue = ['name'],
  debounceDelay = 500,
  maxSelected,
  extraOptionsById = {}
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [dropdownWidth, setDropdownWidth] = useState(0);
  const [openUpward, setOpenUpward] = useState(false);

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const listRef = useRef(null);
  const sentinelRef = useRef(null);

  const checkDropDirection = () => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = 320; // приблизна висота дропдауну (max-height 300 + відступи)
    setOpenUpward(spaceBelow < dropdownHeight);
  };

  const getLabel = (option) => fieldsValue.map(f => option[f]).join(' ');

  // Build selected options list: prefer from current options page, fall back to extraOptionsById cache
  const selectedOptions = value.map(id => {
    const fromOptions = options.find(o => o._id === id);
    if (fromOptions) return fromOptions;
    const cached = extraOptionsById[id];
    if (cached) return { _id: id, ...cached };
    return null;
  }).filter(Boolean);

  useEffect(() => { setFilteredOptions(options); }, [options]);

  useEffect(() => {
    const updateWidth = () => {
      if (wrapperRef.current) setDropdownWidth(wrapperRef.current.offsetWidth);
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setInputValue('');
        if (!onSearch) setFilteredOptions(options);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [options, onSearch]);

  useEffect(() => () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
  }, []);

  useEffect(() => {
    if (!onLoadMore || !hasMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && hasMore) onLoadMore();
      },
      { root: listRef.current, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore, loadingMore, isOpen, filteredOptions]);

  const performSearch = (searchValue) => {
    if (onSearch) {
      onSearch(searchValue);
    } else if (searchValue) {
      const lower = searchValue.toLowerCase();
      setFilteredOptions(
        options.filter(opt =>
          fieldsValue.map(f => opt[f] || '').join(' ').toLowerCase().includes(lower)
        )
      );
    } else {
      setFilteredOptions(options);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    setIsOpen(true);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => performSearch(val), debounceDelay);
  };

  const handleSelect = (option) => {
    const id = option._id;
    let newValue;
    if (value.includes(id)) {
      newValue = value.filter(v => v !== id);
    } else {
      if (maxSelected && value.length >= maxSelected) return;
      newValue = [...value, id];
    }
    onChange(newValue);
    // keep dropdown open, keep search text for further selection
    inputRef.current?.focus();
  };

  const handleRemoveTag = (id, e) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== id));
  };

  const handleFocus = () => {
    if (!disabled) {
      checkDropDirection();
      setIsOpen(true);
      if (onSearch) {
        // lazy load: fire initial search only if options not yet loaded
        if (options.length === 0) onSearch('');
      } else {
        setFilteredOptions(options);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setInputValue('');
    } else if (e.key === 'Enter' && isOpen && filteredOptions.length > 0) {
      e.preventDefault();
      const first = filteredOptions.find(o => !value.includes(o._id));
      if (first) handleSelect(first);
    } else if (e.key === 'ArrowDown' && !isOpen) {
      setIsOpen(true);
    }
  };

  const toggleDropdown = () => {
    if (!disabled) {
      const next = !isOpen;
      if (next) checkDropDirection();
      setIsOpen(next);
      if (next) {
        inputRef.current?.focus();
        if (onSearch) {
          if (options.length === 0) onSearch('');
        } else {
          setFilteredOptions(options);
        }
      } else {
        setInputValue('');
      }
    }
  };

  const inputPlaceholder = value.length > 0
    ? `Обрано ${value.length}`
    : placeholder;

  return (
    <div className={`mas-container ${className}`} ref={wrapperRef}>
      {label && <label className="mas-label">{label}</label>}

      <div className="mas-wrapper">
        {/* Input row */}
        <div className={`mas-input-row ${isOpen ? 'open' : ''} ${error ? 'has-error' : ''} ${disabled ? 'disabled' : ''}`}
          style={isOpen ? (openUpward
            ? { borderRadius: '0 0 6px 6px', borderTop: '1px solid #3b82f6' }
            : { borderRadius: '6px 6px 0 0' }
          ) : {}}
        >
          <input
            ref={inputRef}
            type="text"
            className="mas-search-input"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            placeholder={inputPlaceholder}
            disabled={disabled}
          />
          <div className="mas-controls">
            {value.length > 0 && !disabled && (
              <button
                type="button"
                className="mas-clear-btn"
                onClick={(e) => { e.stopPropagation(); onChange([]); }}
                aria-label="Очистити всі"
                title="Очистити всі"
              >
                ×
              </button>
            )}
            <button
              type="button"
              className={`mas-toggle-btn ${isOpen ? 'open' : ''}`}
              onClick={toggleDropdown}
              disabled={disabled}
              aria-label={isOpen ? 'Згорнути' : 'Розгорнути'}
            >
              ▼
            </button>
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div className="mas-dropdown" style={{
            width: '100%',
            ...(openUpward
              ? { bottom: '100%', top: 'auto', borderRadius: '6px 6px 0 0', borderTop: '1px solid #3b82f6', borderBottom: 'none', marginTop: 0, marginBottom: '-1px' }
              : { top: '100%', bottom: 'auto' }
            )
          }}>
            {loading ? (
              <div className="mas-state-msg">
                <span className="mas-spinner"></span>
                Завантаження...
              </div>
            ) : filteredOptions.length > 0 ? (
              <div className="mas-list" ref={listRef}>
                {filteredOptions.map(option => {
                  const isSelected = value.includes(option._id);
                  return (
                    <div
                      key={option._id}
                      className={`mas-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelect(option)}
                    >
                      <span className={`mas-checkbox ${isSelected ? 'checked' : ''}`}>
                        {isSelected && <span className="mas-checkmark">✓</span>}
                      </span>
                      <span className="mas-item-content">
                        <span className="mas-item-label">{getLabel(option)}</span>
                        {option.description && (
                          <span className="mas-item-desc">{option.description}</span>
                        )}
                      </span>
                    </div>
                  );
                })}
                {hasMore && (
                  <div ref={sentinelRef} className="mas-sentinel">
                    {loadingMore && (
                      <div className="mas-state-msg">
                        <span className="mas-spinner mas-spinner-sm"></span>
                        Завантаження...
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="mas-state-msg">
                {inputValue.length > 0 ? 'Нічого не знайдено' : 'Немає доступних опцій'}
              </div>
            )}
          </div>
        )}

        {error && <div className="mas-error">{error}</div>}

        {/* Selected tags below */}
        {selectedOptions.length > 0 && (
          <div className="mas-tags-list">
            {selectedOptions.map(opt => (
              <span key={opt._id} className="mas-tag">
                <span className="mas-tag-label">{getLabel(opt)}</span>
                {!disabled && (
                  <button
                    type="button"
                    className="mas-tag-remove"
                    onClick={(e) => handleRemoveTag(opt._id, e)}
                    aria-label={`Видалити ${getLabel(opt)}`}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiAutocompleteSelect;